import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import { CmLongLoading, CmScrollBox } from '@/components/uiParts';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { MenuManagementBusinessHoursForm } from '@/pages/menu-managements/business-hours/components/MenuManagementBusinessHoursForm';
import { useMenuManagementBusinessHourForm } from '@/pages/menu-managements/business-hours/hooks/useMenuManagementBusinessHourForm';
import { CanNotUpdateMenuManagementNotice } from '@/pages/menu-managements/components/Alert/CanNotUpdateMenuManagementNotice';
import { MenuManagementEditButtonArea } from '@/pages/menu-managements/components/TopButtonArea';
import { routes } from '@/router';
import { useMenuManagementDeleteBrandMenuHour } from '@/services/BrandMenuHour/hooks/delete/useMenuManagementDeleteBrandMenuHour';

const MenuManagementBusinessHoursEditPage: React.FC = (): JSX.Element => {
  const { id: urlParamsId } = useParams<{ id: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  const {
    associateBrandSelectInput,
    titleJa,
    memo,
    error,
    targetsHeight,
    initialStringifyFormState,
    dayOfWeekPeriods,
    isLoading,
    setIsLoading,
    handleAddMenuHour,
    handleDeleteTime,
    handleChangeStartTimeHour,
    handleChangeStartTimeMinutes,
    handleChangeEndTimeHour,
    handleChangeEndTimeMinutes,
    handleUpdate,
    handleInputTitleJa,
    handleInputMemo,
    initializeEditPageState,
  } = useMenuManagementBusinessHourForm();

  const isRequiredErrorDayOfWeekPeriod = dayOfWeekPeriods.every((dowp) => {
    return dowp.periods.length === 0;
  });

  const isEdit =
    initialStringifyFormState !==
    JSON.stringify([titleJa, memo, dayOfWeekPeriods]);

  const isDisabledSubmitButton = [
    error.titleJa.isError,
    error.memo.isError,
    isRequiredErrorDayOfWeekPeriod,
    !isEdit,
  ].some((error) => error);

  const listUrl = routes.menuManagementBusinessHours.path;

  const handleDelete = () => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementDeleteBrandMenuHour(urlParamsId);
        setIsLoading(false);
        alert('メニュー提供時間を削除しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  useEffect(() => {
    (async () => {
      await initializeEditPageState(urlParamsId);
    })();
  }, [urlParamsId]);

  return (
    <PageLayout
      title={routes.menuManagementBusinessHoursEdit.title}
      associateBrandSelectInput={{
        ...associateBrandSelectInput,
        isDisabled: true,
      }}
      alert={<CanNotUpdateMenuManagementNotice pageName={'メニュー提供時間'} />}
    >
      <MenuManagementEditButtonArea
        name="メニュー提供時間"
        isOpenDeleteOpen={isOpen}
        handleOpenDeleteModal={onOpen}
        handleCloseDeleteModal={onClose}
        {...{
          listUrl,
          urlParamsId,
          isDisabledSubmitButton,
          handleUpdate,
          handleDelete,
        }}
      />

      <CmScrollBox height={`calc(100vh - ${targetsHeight}px)`}>
        <MenuManagementBusinessHoursForm
          {...{
            titleJa,
            memo,
            error,
            dayOfWeekPeriods,
            handleAddMenuHour,
            handleDeleteTime,
            handleChangeStartTimeHour,
            handleChangeStartTimeMinutes,
            handleChangeEndTimeHour,
            handleChangeEndTimeMinutes,
            handleInputTitleJa,
            handleInputMemo,
          }}
        />
      </CmScrollBox>
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default MenuManagementBusinessHoursEditPage;
